import { z } from 'zod';
import { isPercentageMethod } from '@/utils/calculateHalfDayRate';

// Schema for a single role entry
const roleItemSchema = z.object({
  name: z.string(),
  rate: z.union([z.string(), z.number()]).nullable().optional(),
  billingStructure: z.enum(['hourly', 'day_rate']).optional(),
  allowHalfDay: z.boolean().optional(),
  halfDayMethod: z.string().nullable().optional(),
  halfDayMultiplier: z.union([z.string(), z.number()]).nullable().optional(),
  halfDayFixedRate: z.union([z.string(), z.number()]).nullable().optional(),
  halfDayFixedDiscount: z.union([z.string(), z.number()]).nullable().optional(),
}).passthrough();

export const rolesSchema = z
  .object({
    roles: z.array(roleItemSchema).optional(),
    minimumBillableHours: z.union([z.string(), z.number()]).nullable().optional(),
    halfDayHours: z.union([z.string(), z.number()]).nullable().optional(),
    billingStructureConfig: z
      .object({
        minimumBillableHours: z.union([z.string(), z.number()]).nullable().optional(),
        halfDayHours: z.union([z.string(), z.number()]).nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
  })
  .passthrough()
  .superRefine((data, ctx) => {
    const roles = data.roles;

    // --- Array-level checks ---
    if (!roles?.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['roles'],
        message: 'At least one role is required',
      });
      return; // No point continuing without roles
    }

    // Duplicate name+billingStructure combos
    const roleCounts: Record<string, number> = {};
    roles.forEach((r) => {
      const key = `${r.name.trim().toLowerCase()}|${r.billingStructure}`;
      roleCounts[key] = (roleCounts[key] ?? 0) + 1;
    });
    Object.entries(roleCounts).forEach(([_key, count]) => {
      if (count > 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['roles'],
          message: 'A rate for this position and billing structure already exists.',
        });
      }
    });

    // --- Per-role basic checks ---
    roles.forEach((role, index) => {
      if (!role.name.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [`roles[${index}].name`],
          message: 'Role name is required',
        });
      }

      const rateNum = role.rate == null ? null : Number(role.rate);
      if (!role.rate || rateNum === null || isNaN(rateNum) || rateNum <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [`roles[${index}].rate`],
          message: 'Rate must be greater than 0',
        });
      } else if (rateNum > 99999) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [`roles[${index}].rate`],
          message: 'Rate must be less than $99,999',
        });
      }
    });

    // --- Billing structure config ---
    const cfg = {
      minimumBillableHours:
        data.billingStructureConfig?.minimumBillableHours ??
        (data as Record<string, unknown>).minimumBillableHours ??
        null,
      halfDayHours:
        data.billingStructureConfig?.halfDayHours ??
        (data as Record<string, unknown>).halfDayHours ??
        null,
    };

    const anyDayRateRole = roles.some((r) => r.billingStructure === 'day_rate');

    // Minimum billable hours required for day-rate roles
    if (anyDayRateRole) {
      const fdh = cfg.minimumBillableHours === null ? null : Number(cfg.minimumBillableHours);
      if (fdh === null || isNaN(fdh as number)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['minimumBillableHours'],
          message: 'Minimum Billable Hours is required for day rate roles',
        });
      } else if ((fdh as number) <= 0 || (fdh as number) > 24) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['minimumBillableHours'],
          message: 'Minimum Billable Hours must be between 1 and 24',
        });
      }
    }

    // Half-day threshold
    const allowHalfDay = roles.some((r) => r.billingStructure === 'day_rate' && r.allowHalfDay);

    if (allowHalfDay) {
      const fullDay =
        cfg.minimumBillableHours === null ? null : Number(cfg.minimumBillableHours);
      const halfDay = cfg.halfDayHours === null ? null : Number(cfg.halfDayHours);

      if (halfDay === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['halfDayHours'],
          message: 'Half-Day Threshold is required when half-day billing is enabled',
        });
      } else if (halfDay <= 0 || (fullDay !== null && halfDay >= fullDay)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['halfDayHours'],
          message: 'Half-Day Threshold must be less than minimum billable hours',
        });
      }
    }

    // --- Per-role half-day validation ---
    roles.forEach((role, index) => {
      if (role.billingStructure !== 'day_rate' || !role.allowHalfDay) return;

      if (!role.halfDayMethod) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [`roles[${index}].halfDayMethod`],
          message: 'Half-day rate method is required',
        });
        return;
      }

      if (isPercentageMethod(role.halfDayMethod as never)) {
        const multiplier =
          role.halfDayMultiplier === null ? null : Number(role.halfDayMultiplier);
        if (multiplier === null || multiplier <= 0 || multiplier >= 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [`roles[${index}].halfDayMultiplier`],
            message:
              role.halfDayMethod === 'pct_discount'
                ? 'Discount percentage must be between 0% and 100%'
                : 'Half Day Rate Percentage must be between 0% and 100%',
          });
        }
      } else if (role.halfDayMethod === 'fixed') {
        const fixedRate =
          role.halfDayFixedRate === null ? null : Number(role.halfDayFixedRate);
        if (fixedRate === null || fixedRate <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [`roles[${index}].halfDayFixedRate`],
            message: 'Half Day Rate must be a positive number',
          });
        }
      } else if (role.halfDayMethod === 'fixed_discount') {
        const fixedDiscount =
          role.halfDayFixedDiscount === null ? null : Number(role.halfDayFixedDiscount);
        const rateNum = role.rate == null ? null : Number(role.rate);

        if (fixedDiscount === null || fixedDiscount <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [`roles[${index}].halfDayFixedDiscount`],
            message: 'Discount must be a positive number',
          });
        }
        if (
          fixedDiscount !== null &&
          rateNum !== null &&
          rateNum > 0 &&
          fixedDiscount >= rateNum
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [`roles[${index}].halfDayFixedDiscount`],
            message: `Discount must be less than the day rate ($${rateNum.toFixed(2)})`,
          });
        }
      }
    });
  });

export type RolesSchemaData = z.infer<typeof rolesSchema>;
